/**
 * ============================================================
 * Style Playground — Auto-generated metadata
 * ============================================================
 * Device: desktop
 * Language: Chinese
 *
 * ## Style Direction (from analysis)
 * <style-name>SoftElevation</style-name>
 * **Mode**: Light
 * **Style Recipe**: Flat 2.0 / Soft Shadow + Floating / Borderless + Balanced
 * **Implementation Techniques**: Utilizes continuous curves (squircles, e.g., 16px radius for large cards, 8px for inner elements) to create a highly polished, tactile feel. Components and dashboard widgets float on a slightly off-white background, elevated by multi-layered, highly diffused drop shadows (e.g., a tight dark shadow for contact, plus a wide, low-opacity shadow for ambient depth). Typography uses a geometric sans-serif (like Plus Jakarta Sans) for a modern, friendly tech vibe. No hard borders are used on primary containers; separation is achieved purely through shadow and Z-axis layering.
 * **Color Direction**: Warm off-white base (`#F8F9FA`) with pure stark white (`#FFFFFF`) for elevated surface cards. Text is a soft charcoal (`#1C1E21`) rather than harsh black. The primary accent is a vibrant Electric Indigo or Ultramarine blue, providing a sharp, energetic contrast against the soft, neutral environment without falling into "boring tech blue."
 * ============================================================
 */

import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useLocation } from 'react-router-dom';

/* ─── Mock 数据 ─── */
const mockComponents = [
  { id: '1', name: '按钮', category: '基础', status: '已发布', version: 'v1.2.0', variants: 12, icon: 'lucide:mouse-pointer-click', updated: '2 小时前', author: '张达', desc: '基础交互按钮组件，支持多种变体、尺寸和状态切换。适用于表单提交、操作触发等场景。' },
  { id: '2', name: '输入框', category: '表单', status: '已发布', version: 'v2.0.1', variants: 8, icon: 'lucide:text-cursor-input', updated: '1 天前', author: '李安', desc: '文本输入组件，支持单行、多行、密码及搜索模式。' },
  { id: '3', name: '复选框', category: '表单', status: '草稿', version: 'v0.9.0', variants: 4, icon: 'lucide:check-square', updated: '5 小时前', author: '李安', desc: '多选交互组件，支持分组和不确定态。' },
  { id: '4', name: '弹窗', category: '反馈', status: '审核中', version: 'v2.1.1', variants: 6, icon: 'lucide:message-square', updated: '昨天', author: '马凯', desc: '模态弹窗组件，支持确认、信息和表单模式。' },
  { id: '5', name: '下拉菜单', category: '导航', status: '已发布', version: 'v1.0.4', variants: 5, icon: 'lucide:chevron-down-square', updated: '昨天', author: '张达', desc: '下拉选择与菜单组件，支持单选和多级嵌套。' },
  { id: '6', name: '标签页', category: '导航', status: '已发布', version: 'v1.3.0', variants: 3, icon: 'lucide:layout-list', updated: '3 天前', author: '马凯', desc: '内容切换标签页，支持横向和纵向布局。' },
  { id: '7', name: '提示条', category: '反馈', status: '已发布', version: 'v1.1.0', variants: 4, icon: 'lucide:alert-circle', updated: '2 天前', author: '李安', desc: '消息提示组件，支持成功、警告、错误和信息类型。' },
  { id: '8', name: '表格', category: '数据展示', status: '已发布', version: 'v3.0.2', variants: 7, icon: 'lucide:table', updated: '4 天前', author: '张达', desc: '数据表格组件，支持排序、筛选和分页。' },
  { id: '9', name: '卡片', category: '布局', status: '草稿', version: 'v0.5.0', variants: 9, icon: 'lucide:square', updated: '1 周前', author: '马凯', desc: '内容容器卡片，支持多种布局和交互状态。' },
];

const mockVersions = [
  { id: 'v1', version: '1.2.0', date: '2024-01-15', author: '张达', notes: '新增 loading 状态、icon 插槽，优化 hover 动画过渡效果' },
  { id: 'v2', version: '1.1.0', date: '2024-01-08', author: '李安', notes: '新增 outline 和 ghost 变体，修复聚焦态圆角异常' },
  { id: 'v3', version: '1.0.2', date: '2023-12-20', author: '张达', notes: '修复暗色模式下文字颜色对比度不足问题' },
  { id: 'v4', version: '1.0.1', date: '2023-12-15', author: '马凯', notes: '补充无障碍 ARIA 标签，优化键盘导航' },
  { id: 'v5', version: '1.0.0', date: '2023-12-01', author: '张达', notes: '首次发布，包含 primary、secondary、danger 三种变体' },
];

const statusStyles: Record<string, string> = {
  '已发布': 'bg-emerald-50 text-emerald-600',
  '草稿': 'bg-amber-50 text-amber-600',
  '审核中': 'bg-sky-50 text-sky-600',
};

/**
 * URL 视图映射：通过 ?view= 查询参数控制初始显示的子页面。
 * 支持的值：
 * - components-list → 组件库列表
 * - components-detail → 组件详情（默认显示第一个组件）
 * - components-new → 新建组件
 * - components-versions → 版本历史（默认显示第一个组件）
 * - tokens → Design Tokens
 * - 无参数或其他 → 概览仪表盘
 */
function parseInitialView(search: string): { nav: string; subPage: string } {
  const params = new URLSearchParams(search);
  const view = params.get('view');
  if (view === 'components-list') return { nav: 'components', subPage: 'list' };
  if (view === 'components-detail') return { nav: 'components', subPage: 'detail:1' };
  if (view === 'components-new') return { nav: 'components', subPage: 'new' };
  if (view === 'components-versions') return { nav: 'components', subPage: 'versions:1' };
  if (view === 'tokens') return { nav: 'tokens', subPage: 'list' };
  return { nav: 'dashboard', subPage: 'list' };
}

export default function ComponentHubDashboard() {
  const location = useLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally read only on mount
  const initialView = useMemo(() => parseInitialView(location.search), []);
  const [activeNav, setActiveNav] = useState(initialView.nav);
  // 组件库子页面状态: list | detail:id | new | versions:id
  const [compSubPage, setCompSubPage] = useState<string>(initialView.subPage);

  const handleNavClick = (key: string) => {
    setActiveNav(key);
    if (key === 'components') setCompSubPage('list');
  };

  /* ─── 动态 Header ─── */
  const getHeader = () => {
    if (activeNav === 'dashboard') return { title: '概览', desc: '组件库统计数据与近期动态' };
    if (activeNav === 'tokens') return { title: 'Design Tokens', desc: '管理设计变量与主题配置' };
    if (activeNav !== 'components') return { title: '概览', desc: '' };

    if (compSubPage === 'list') return { title: '组件库', desc: '浏览和管理所有设计组件' };
    if (compSubPage === 'new') return { title: '新建组件', desc: '创建新的设计组件' };
    if (compSubPage.startsWith('detail:')) {
      const comp = mockComponents.find(c => c.id === compSubPage.split(':')[1]);
      return { title: comp?.name ?? '组件详情', desc: comp?.desc ?? '' };
    }
    if (compSubPage.startsWith('versions:')) {
      const comp = mockComponents.find(c => c.id === compSubPage.split(':')[1]);
      return { title: '版本历史', desc: `${comp?.name ?? ''} · 当前 ${comp?.version ?? ''}` };
    }
    return { title: '组件库', desc: '' };
  };

  const header = getHeader();
  const showBackButton = activeNav === 'components' && compSubPage !== 'list';

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] text-[#1A1D25] font-['Noto_Sans_SC',sans-serif] overflow-hidden selection:bg-[#1D263A] selection:text-white">
      {/* 侧边栏 */}
      <div className="w-64 p-6 pr-0 flex flex-col h-full">
        <div className="bg-white rounded-[24px] h-full shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col overflow-hidden">
          <div className="p-6 flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-[#1D263A] flex items-center justify-center shadow-[0_4px_12px_rgba(29,38,58,0.3)]">
              <Icon icon="lucide:box" className="text-white text-xl" />
            </div>
            <span className="font-bold text-lg tracking-tight">ComponentHub</span>
          </div>

          <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
            <div className="text-xs font-semibold text-gray-400 tracking-wider mb-3 px-3 mt-4">主菜单</div>
            {[
              { key: 'dashboard', icon: 'lucide:layout-dashboard', label: '概览' },
              { key: 'components', icon: 'lucide:layers', label: '组件库' },
              { key: 'tokens', icon: 'lucide:palette', label: 'Design Tokens' },
            ].map(nav => (
              <button key={nav.key} onClick={() => handleNavClick(nav.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${activeNav === nav.key ? 'bg-[#1D263A]/5 text-[#1D263A] font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                <Icon icon={nav.icon} className="text-lg" />
                <span>{nav.label}</span>
              </button>
            ))}
            <div className="text-xs font-semibold text-gray-400 tracking-wider mb-3 px-3 mt-8">系统</div>
            {[
              { key: 'changelog', icon: 'lucide:history', label: '变更日志' },
              { key: 'categories', icon: 'lucide:folder-tree', label: '分类管理' },
            ].map(nav => (
              <button key={nav.key} onClick={() => handleNavClick(nav.key)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${activeNav === nav.key ? 'bg-[#1D263A]/5 text-[#1D263A] font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                <Icon icon={nav.icon} className="text-lg" />
                <span>{nav.label}</span>
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-100 mt-auto">
            <button className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#1D263A] to-[#151D2E] text-white flex items-center justify-center font-medium text-sm shadow-sm">张</div>
              <div className="flex flex-col text-left">
                <span className="text-sm font-medium text-gray-900">张达</span>
                <span className="text-xs text-gray-500">首席设计师</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0">
        {/* 头部 */}
        <header className="flex items-center justify-between mb-8 px-2">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <button onClick={() => {
                if (compSubPage.startsWith('versions:')) setCompSubPage('detail:' + compSubPage.split(':')[1]);
                else setCompSubPage('list');
              }} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all">
                <Icon icon="lucide:arrow-left" className="text-xl" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{header.title}</h1>
                {compSubPage.startsWith('detail:') && (() => {
                  const comp = mockComponents.find(c => c.id === compSubPage.split(':')[1]);
                  return comp ? (
                    <>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusStyles[comp.status] || ''}`}>{comp.status}</span>
                      <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">{comp.version}</span>
                    </>
                  ) : null;
                })()}
              </div>
              {header.desc && <p className="text-gray-500 mt-1">{header.desc}</p>}
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* 组件详情操作按钮 */}
            {compSubPage.startsWith('detail:') && (
              <div className="flex items-center gap-2 mr-2">
                <button className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2"><Icon icon="lucide:play" />预览</button>
                <button onClick={() => setCompSubPage('versions:' + compSubPage.split(':')[1])} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-700 bg-white shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all flex items-center gap-2"><Icon icon="lucide:history" />版本</button>
              </div>
            )}
            {/* 版本历史操作 */}
            {compSubPage.startsWith('versions:') && (
              <button className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm flex items-center gap-2 mr-2"><Icon icon="lucide:upload" />发布新版本</button>
            )}
            {/* 新建组件操作 */}
            {compSubPage === 'new' && (
              <button className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm">创建组件</button>
            )}

            <div className="relative group">
              <Icon icon="lucide:search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-[#1D263A] transition-colors" />
              <input type="text" placeholder="搜索组件..." className="w-64 pl-10 pr-4 py-2.5 bg-white rounded-full text-sm outline-none shadow-[0_2px_10px_rgb(0,0,0,0.02)] border border-transparent focus:border-[#1D263A]/10 focus:shadow-[0_4px_12px_rgb(29,38,58,0.08)] transition-all placeholder-gray-400" />
            </div>
            <button className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all relative">
              <Icon icon="lucide:bell" className="text-xl" />
              <span className="absolute top-2 right-2.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white"></span>
            </button>
            <div className="h-6 w-px bg-gray-200"></div>
            <div className="flex items-center gap-3 pl-1 cursor-pointer group">
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#1D263A] to-[#151D2E] text-white flex items-center justify-center font-medium text-sm shadow-[0_2px_8px_rgba(29,38,58,0.25)] group-hover:shadow-[0_4px_12px_rgba(29,38,58,0.35)] transition-all">张</div>
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900 leading-tight">张达</span>
                <span className="text-xs text-gray-400 leading-tight">首席设计师</span>
              </div>
              <Icon icon="lucide:chevron-down" className="text-sm text-gray-400 group-hover:text-gray-600 transition-colors" />
            </div>
          </div>
        </header>

        {/* 可滚动内容区 */}
        <div className="flex-1 overflow-y-auto pb-8 px-2 custom-scrollbar">
          {activeNav === 'dashboard' && <DashboardContent />}
          {activeNav === 'components' && <ComponentsContent subPage={compSubPage} setSubPage={setCompSubPage} />}
          {activeNav === 'tokens' && <TokensContent />}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   概览 Tab
   ═══════════════════════════════════════════════ */
function DashboardContent() {
  const stats = [
    { label: '组件总数', value: '142', trend: '本周新增 12', icon: 'lucide:layers' },
    { label: '草稿中', value: '12', trend: '3 个待审核', icon: 'lucide:edit-3' },
    { label: '审核中', value: '5', trend: '2 个紧急', icon: 'lucide:eye' },
    { label: '已发布', value: '125', trend: '本周新增 8', icon: 'lucide:check-circle' },
  ];

  const recentUpdates = [
    { name: '按钮', version: 'v1.2.0', status: '已发布', time: '2 小时前', user: '张达' },
    { name: '复选框', version: 'v0.9.0', status: '草稿', time: '5 小时前', user: '李安' },
    { name: '弹窗', version: 'v2.1.1', status: '审核中', time: '昨天', user: '马凯' },
    { name: '下拉菜单', version: 'v1.0.4', status: '已发布', time: '昨天', user: '张达' },
    { name: '提示条', version: 'v1.1.0', status: '已发布', time: '2 天前', user: '李安' },
  ];

  const activities = [
    { text: '张达 发布了按钮组件 v1.2.0', time: '2 小时前' },
    { text: '李安 创建了复选框组件草稿', time: '5 小时前' },
    { text: '马凯 提交了弹窗组件审核', time: '1 天前' },
    { text: '张达 发布了下拉菜单组件 v1.0.4', time: '1 天前' },
  ];

  return (
    <>
      <div className="grid grid-cols-4 gap-6 mb-8">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-shadow duration-300 flex flex-col gap-4 group">
            <div className="flex justify-between items-start">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-gray-50 text-gray-500 group-hover:scale-110 transition-transform duration-300 ${idx === 0 || idx === 2 ? 'group-hover:bg-[#1D263A]/5 group-hover:text-[#1D263A]' : ''} ${idx === 1 ? 'group-hover:bg-amber-50 group-hover:text-amber-600' : ''} ${idx === 3 ? 'group-hover:bg-emerald-50 group-hover:text-emerald-600' : ''}`}>
                <Icon icon={stat.icon} className="text-2xl" />
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">{stat.trend}</span>
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 tracking-tight">{stat.value}</h3>
              <p className="text-sm text-gray-500 font-medium mt-1">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">最近更新</h2>
              <button className="text-sm font-medium text-[#1D263A] hover:text-[#151D2E] flex items-center gap-1">查看全部 <Icon icon="lucide:arrow-right" /></button>
            </div>
            <div className="space-y-4">
              {recentUpdates.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-2xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100 group cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 group-hover:bg-white group-hover:shadow-sm transition-all"><Icon icon="lucide:component" className="text-xl" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{item.name}</span>
                        <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-md">{item.version}</span>
                      </div>
                      <span className="text-sm text-gray-500">{item.time} 由 {item.user} 更新</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusStyles[item.status] || ''}`}>{item.status}</span>
                    <button className="text-gray-400 hover:text-[#1D263A] opacity-0 group-hover:opacity-100 transition-all"><Icon icon="lucide:chevron-right" className="text-xl" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-1 flex flex-col gap-6">
          <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6 flex flex-col gap-4">
            <button className="w-full bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-3.5 px-4 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] hover:shadow-[0_6px_16px_rgba(29,38,58,0.35)] transition-all flex items-center justify-center gap-2"><Icon icon="lucide:plus" className="text-xl" /><span>新建组件</span></button>
            <button className="w-full bg-white border-2 border-gray-100 hover:border-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2"><Icon icon="lucide:palette" className="text-xl text-gray-400" /><span>管理 Tokens</span></button>
          </div>
          <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6 flex-1">
            <h2 className="text-lg font-bold text-gray-900 mb-6">团队动态</h2>
            <div className="relative border-l-2 border-gray-100 ml-3 space-y-8 pb-4">
              {activities.map((activity, idx) => (
                <div key={idx} className="relative pl-6">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-white border-2 border-[#1D263A]/15 shadow-sm"></div>
                  <p className="text-sm text-gray-800 font-medium leading-snug">{activity.text}</p>
                  <span className="text-xs text-gray-400 mt-1 block">{activity.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   组件库 Tab（含子页面导航）
   ═══════════════════════════════════════════════ */
function ComponentsContent({ subPage, setSubPage }: { subPage: string; setSubPage: (p: string) => void }) {
  if (subPage === 'list') return <CompListView onSelect={id => setSubPage('detail:' + id)} onNew={() => setSubPage('new')} />;
  if (subPage === 'new') return <CompNewView />;
  if (subPage.startsWith('detail:')) return <CompDetailView compId={subPage.split(':')[1]} />;
  if (subPage.startsWith('versions:')) return <CompVersionsView compId={subPage.split(':')[1]} />;
  return null;
}

/* ─── 组件列表视图 ─── */
function CompListView({ onSelect, onNew }: { onSelect: (id: string) => void; onNew: () => void }) {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [activeCategory, setActiveCategory] = useState('全部');
  const categories = ['全部', '基础', '表单', '反馈', '导航', '数据展示', '布局'];
  const filtered = activeCategory === '全部' ? mockComponents : mockComponents.filter(c => c.category === activeCategory);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${activeCategory === cat ? 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.25)]' : 'bg-white text-gray-600 hover:bg-gray-50 shadow-[0_2px_10px_rgb(0,0,0,0.02)]'}`}>{cat}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={onNew} className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2 px-4 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm flex items-center gap-2"><Icon icon="lucide:plus" className="text-lg" />新建</button>
          <div className="flex items-center gap-2 bg-white rounded-xl p-1 shadow-[0_2px_10px_rgb(0,0,0,0.02)]">
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'text-gray-400 hover:text-gray-600'}`}><Icon icon="lucide:grid-3x3" className="text-lg" /></button>
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'text-gray-400 hover:text-gray-600'}`}><Icon icon="lucide:list" className="text-lg" /></button>
          </div>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-3 gap-6">
          {filtered.map(comp => (
            <div key={comp.id} onClick={() => onSelect(comp.id)} className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 group cursor-pointer overflow-hidden">
              <div className="h-40 bg-gray-50 flex items-center justify-center relative overflow-hidden">
                <Icon icon={comp.icon} className="text-5xl text-gray-300 group-hover:text-[#1D263A]/30 group-hover:scale-110 transition-all duration-300" />
                <div className="absolute top-3 right-3"><span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusStyles[comp.status] || ''}`}>{comp.status}</span></div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-gray-900">{comp.name}</h3>
                  <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md">{comp.version}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{comp.category} · {comp.variants} 个变体</span>
                  <span className="text-xs text-gray-400">{comp.updated}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden">
          {filtered.map((comp, idx) => (
            <div key={comp.id} onClick={() => onSelect(comp.id)} className={`flex items-center justify-between p-5 hover:bg-gray-50 transition-colors cursor-pointer group ${idx > 0 ? 'border-t border-gray-50' : ''}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-[#1D263A]/5 group-hover:text-[#1D263A] transition-all"><Icon icon={comp.icon} className="text-xl" /></div>
                <div>
                  <div className="flex items-center gap-2"><span className="font-semibold text-gray-900">{comp.name}</span><span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md">{comp.version}</span></div>
                  <span className="text-sm text-gray-400">{comp.category} · {comp.variants} 个变体 · {comp.updated}</span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${statusStyles[comp.status] || ''}`}>{comp.status}</span>
                <Icon icon="lucide:chevron-right" className="text-lg text-gray-300 group-hover:text-[#1D263A] transition-colors" />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ─── 组件详情视图 ─── */
function CompDetailView({ compId }: { compId: string }) {
  const comp = mockComponents.find(c => c.id === compId);
  const [activeTab, setActiveTab] = useState('design');
  if (!comp) return null;

  const detailTabs = [
    { key: 'design', label: '设计稿', icon: 'lucide:image' },
    { key: 'code', label: '代码', icon: 'lucide:code-2' },
    { key: 'props', label: 'Props', icon: 'lucide:list' },
    { key: 'guidelines', label: '使用指南', icon: 'lucide:book-open' },
  ];

  return (
    <>
      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {detailTabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-[#1D263A] shadow-[0_2px_10px_rgb(0,0,0,0.04)]' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}>
            <Icon icon={t.icon} className="text-lg" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab 内容 */}
      {activeTab === 'design' && (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Icon icon={comp.icon} className="text-8xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">{comp.name} 组件预览</p>
            <p className="text-sm text-gray-400 mt-1">{comp.variants} 个变体 · {comp.category}</p>
          </div>
        </div>
      )}

      {activeTab === 'code' && (
        <div className="bg-gray-900 text-gray-100 rounded-2xl p-6 overflow-x-auto">
          <pre className="text-sm font-mono whitespace-pre-wrap">{`import { ${comp.name} } from '@component-hub/ui';

export function Demo() {
  return (
    <${comp.name}
      variant="primary"
      size="md"
      onClick={() => console.log('clicked')}
    >
      ${comp.name} 示例
    </${comp.name}>
  );
}`}</pre>
        </div>
      )}

      {activeTab === 'props' && (
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 text-left">
              <th className="px-6 py-4 font-semibold text-gray-500">属性名</th>
              <th className="px-6 py-4 font-semibold text-gray-500">类型</th>
              <th className="px-6 py-4 font-semibold text-gray-500">默认值</th>
              <th className="px-6 py-4 font-semibold text-gray-500">说明</th>
            </tr></thead>
            <tbody>
              {[
                ['variant', "'primary' | 'secondary' | 'outline' | 'ghost' | 'danger'", "'primary'", '按钮变体样式'],
                ['size', "'xs' | 'sm' | 'md' | 'lg' | 'xl'", "'md'", '按钮尺寸'],
                ['disabled', 'boolean', 'false', '是否禁用'],
                ['loading', 'boolean', 'false', '是否显示加载状态'],
                ['icon', 'ReactNode', '-', '前置图标'],
                ['onClick', '() => void', '-', '点击回调'],
              ].map(([name, type, def, desc], i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-3.5 font-mono text-[#1D263A] font-medium">{name}</td>
                  <td className="px-6 py-3.5 font-mono text-gray-500 text-xs">{type}</td>
                  <td className="px-6 py-3.5 font-mono text-gray-400 text-xs">{def}</td>
                  <td className="px-6 py-3.5 text-gray-600">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'guidelines' && (
        <div className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] space-y-6">
          <div>
            <h3 className="font-bold text-gray-900 mb-3">使用场景</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2"><Icon icon="lucide:check" className="text-emerald-500 mt-0.5 shrink-0" />用于触发即时操作，如表单提交、确认对话</li>
              <li className="flex items-start gap-2"><Icon icon="lucide:check" className="text-emerald-500 mt-0.5 shrink-0" />工具栏操作按钮使用 ghost 或 outline 变体</li>
              <li className="flex items-start gap-2"><Icon icon="lucide:check" className="text-emerald-500 mt-0.5 shrink-0" />危险操作使用 danger 变体并添加二次确认</li>
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-emerald-50/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><Icon icon="lucide:check-circle" className="text-emerald-600" /><span className="font-bold text-emerald-700 text-sm">推荐</span></div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li>每个页面限制 1 个 primary 按钮</li>
                <li>按钮文案使用动词开头（如"提交""保存"）</li>
                <li>按钮组保持尺寸一致</li>
              </ul>
            </div>
            <div className="bg-red-50/50 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3"><Icon icon="lucide:x-circle" className="text-red-600" /><span className="font-bold text-red-700 text-sm">避免</span></div>
              <ul className="space-y-1.5 text-sm text-gray-600">
                <li>同一区域使用多个 primary 按钮</li>
                <li>按钮文案过长（超过 6 个字）</li>
                <li>禁用态不提供提示说明</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── 新建组件视图 ─── */
function CompNewView() {
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
  const [previewVariant, setPreviewVariant] = useState('primary');
  const [previewRadius, setPreviewRadius] = useState('12');
  const isDark = previewTheme === 'dark';

  const variantStyles: Record<string, string> = {
    primary: 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.25)]',
    secondary: isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700',
    outline: isDark ? 'bg-transparent border-2 border-gray-500 text-gray-200' : 'bg-transparent border-2 border-gray-200 text-gray-700',
    ghost: isDark ? 'bg-transparent text-gray-300' : 'bg-transparent text-gray-600',
    danger: 'bg-red-600 text-white shadow-[0_4px_12px_rgba(239,68,68,0.25)]',
  };

  const inputCls = "w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15 transition-all placeholder-gray-400";

  return (
    <div className="flex gap-6 h-full">
      {/* 左侧表单 */}
      <div className="w-[55%] overflow-y-auto pr-2 custom-scrollbar">
        <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-8 space-y-6">
          <section>
            <h2 className="text-base font-bold mb-4">基本信息</h2>
            <div className="flex flex-col gap-4">
              <div><label className="text-sm font-medium text-gray-600 mb-1.5 block">组件名称 *</label><input type="text" placeholder="例如：Button" className={inputCls} /></div>
              <div><label className="text-sm font-medium text-gray-600 mb-1.5 block">分类</label>
                <select className={inputCls}><option>请选择分类</option><option>基础</option><option>表单</option><option>反馈</option><option>导航</option><option>数据展示</option><option>布局</option></select>
              </div>
              <div><label className="text-sm font-medium text-gray-600 mb-1.5 block">描述</label><textarea placeholder="组件的用途和使用场景..." rows={3} className={inputCls + ' resize-none'} /></div>
              <div><label className="text-sm font-medium text-gray-600 mb-1.5 block">标签</label><input type="text" placeholder="用逗号分隔，例如：基础,表单,交互" className={inputCls} /></div>
            </div>
          </section>
          <section>
            <h2 className="text-base font-bold mb-4">代码片段</h2>
            <textarea placeholder="粘贴组件代码示例..." rows={6} className={inputCls + ' resize-none font-mono text-xs'} />
          </section>
          <section>
            <h2 className="text-base font-bold mb-4">Props / API 文档</h2>
            <textarea placeholder="Markdown 格式的属性说明表格..." rows={4} className={inputCls + ' resize-none font-mono text-xs'} />
          </section>
        </div>
      </div>

      {/* 右侧预览 */}
      <div className="w-[45%] bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2"><Icon icon="lucide:eye" className="text-lg text-gray-400" /><span className="text-sm font-bold text-gray-900">实时预览</span></div>
          <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg p-1">
            <button onClick={() => setPreviewTheme('light')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${!isDark ? 'bg-white text-[#1D263A] shadow-sm' : 'text-gray-500'}`}><Icon icon="lucide:sun" className="text-sm" /></button>
            <button onClick={() => setPreviewTheme('dark')} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${isDark ? 'bg-gray-700 text-white shadow-sm' : 'text-gray-500'}`}><Icon icon="lucide:moon" className="text-sm" /></button>
          </div>
        </div>

        <div className={`flex-1 flex items-center justify-center p-6 transition-colors duration-300 ${isDark ? 'bg-gray-900' : "bg-[image:linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0),linear-gradient(45deg,#f0f0f0_25%,transparent_25%,transparent_75%,#f0f0f0_75%,#f0f0f0)] bg-[size:16px_16px] bg-[position:0_0,8px_8px]"}`}>
          <div className="flex flex-col items-center gap-6">
            <div className={`text-xs font-mono px-2.5 py-1 rounded-md ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white/80 text-gray-500 shadow-sm'}`}>&lt;组件名称 /&gt;</div>
            <button className={`font-medium transition-all px-5 py-2.5 text-sm ${variantStyles[previewVariant]}`} style={{ borderRadius: `${previewRadius}px` }}>组件名称</button>
            <div className="flex items-center gap-2">
              {Object.keys(variantStyles).map(v => (
                <button key={v} onClick={() => setPreviewVariant(v)} className={`px-3 py-1.5 text-xs font-medium transition-all ${variantStyles[v]} ${v === previewVariant ? 'ring-2 ring-[#1D263A]/20 ring-offset-2' : 'opacity-60'}`} style={{ borderRadius: `${previewRadius}px` }}>{v}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-5 py-4 bg-white">
          <div className="flex items-center justify-between mb-2"><label className="text-xs font-semibold text-gray-400">圆角</label><span className="text-xs font-mono text-gray-500">{previewRadius}px</span></div>
          <input type="range" min="0" max="24" value={previewRadius} onChange={e => setPreviewRadius(e.target.value)} className="w-full h-1.5 bg-gray-100 rounded-full appearance-none cursor-pointer accent-[#1D263A]" />
        </div>
      </div>
    </div>
  );
}

/* ─── 版本历史视图 ─── */
function CompVersionsView({ compId }: { compId: string }) {
  const comp = mockComponents.find(c => c.id === compId);
  const [selected, setSelected] = useState<string[]>([]);

  const toggleVersion = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(v => v !== id) : prev.length < 2 ? [...prev, id] : prev);
  };

  return (
    <div className="relative">
      <div className="space-y-4">
        {mockVersions.map((v, idx) => (
          <div key={v.id} onClick={() => toggleVersion(v.id)} className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer ${selected.includes(v.id) ? 'border-[#1D263A]/15 bg-[#1D263A]/5' : 'border-transparent bg-white shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]'}`}>
            {/* 时间线 */}
            <div className="flex flex-col items-center shrink-0">
              <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${selected.includes(v.id) ? 'bg-[#1D263A] border-[#1D263A]' : 'border-gray-300'}`}>
                {selected.includes(v.id) && <Icon icon="lucide:check" className="text-white text-sm" />}
              </div>
              {idx < mockVersions.length - 1 && <div className="w-0.5 h-8 bg-gray-200 mt-2"></div>}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-base font-bold font-mono">v{v.version}</span>
                <span className="text-xs text-gray-400">{v.date}</span>
                <span className="text-xs text-gray-500">by {v.author}</span>
                {idx === 0 && <span className="px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-600 rounded-full">当前版本</span>}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{v.notes}</p>
            </div>
          </div>
        ))}
      </div>

      {selected.length === 2 && (
        <div className="sticky bottom-4 mt-6 flex justify-center">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.1)] px-6 py-4 flex items-center gap-4">
            <span className="text-sm font-medium">已选择 2 个版本</span>
            <button className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2 px-5 rounded-xl text-sm transition-all">对比版本</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   Design Tokens Tab
   ═══════════════════════════════════════════════ */
function TokensContent() {
  const [activeView, setActiveView] = useState<'type' | 'layer'>('type');

  const tokenCategories = [
    { name: '颜色', icon: 'lucide:palette', count: 48, preview: ['#1D263A', '#3B82F6', '#10B981', '#F59E0B', '#EF4444'] },
    { name: '字体', icon: 'lucide:type', count: 24, preview: [] },
    { name: '间距', icon: 'lucide:move', count: 16, preview: [] },
    { name: '圆角', icon: 'lucide:square', count: 8, preview: [] },
    { name: '阴影', icon: 'lucide:sun', count: 12, preview: [] },
    { name: '层级', icon: 'lucide:layers', count: 6, preview: [] },
  ];

  const themes = [
    { name: '默认主题', active: true, tokens: 114 },
    { name: '深色主题', active: false, tokens: 114 },
    { name: '高对比度', active: false, tokens: 98 },
  ];

  const recentTokens = [
    { name: 'color-primary', value: '#1D263A', category: '颜色', updated: '1 小时前' },
    { name: 'color-success', value: '#10B981', category: '颜色', updated: '3 小时前' },
    { name: 'font-size-lg', value: '18px', category: '字体', updated: '昨天' },
    { name: 'spacing-md', value: '16px', category: '间距', updated: '昨天' },
    { name: 'radius-lg', value: '16px', category: '圆角', updated: '2 天前' },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {themes.map((theme, idx) => (
            <button key={idx} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${theme.active ? 'bg-[#1D263A] text-white shadow-[0_4px_12px_rgba(29,38,58,0.25)]' : 'bg-white text-gray-600 hover:bg-gray-50 shadow-[0_2px_10px_rgb(0,0,0,0.02)]'}`}>
              {theme.name}<span className={`ml-2 text-xs ${theme.active ? 'text-white/60' : 'text-gray-400'}`}>{theme.tokens}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl p-1 shadow-[0_2px_10px_rgb(0,0,0,0.02)]">
          <button onClick={() => setActiveView('type')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeView === 'type' ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'text-gray-400 hover:text-gray-600'}`}>按类型</button>
          <button onClick={() => setActiveView('layer')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeView === 'layer' ? 'bg-[#1D263A]/5 text-[#1D263A]' : 'text-gray-400 hover:text-gray-600'}`}>按层级</button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <div className="grid grid-cols-3 gap-4">
            {tokenCategories.map((cat, idx) => (
              <div key={idx} className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all duration-300 p-5 cursor-pointer group">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-[#1D263A]/5 group-hover:text-[#1D263A] transition-all"><Icon icon={cat.icon} className="text-xl" /></div>
                  <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">{cat.count} 个</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{cat.name}</h3>
                {cat.preview.length > 0 ? (
                  <div className="flex items-center gap-1.5">{cat.preview.map((color, ci) => (<div key={ci} className="w-5 h-5 rounded-md shadow-sm" style={{ backgroundColor: color }}></div>))}</div>
                ) : (
                  <div className="flex items-center gap-1.5"><div className="h-2 bg-gray-100 rounded-full w-8"></div><div className="h-2 bg-gray-100 rounded-full w-12"></div><div className="h-2 bg-gray-100 rounded-full w-6"></div></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-1">
          <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">最近修改</h2>
            <div className="space-y-4">
              {recentTokens.map((token, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group">
                  {token.category === '颜色' ? (
                    <div className="w-8 h-8 rounded-lg shadow-sm shrink-0" style={{ backgroundColor: token.value }}></div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-xs font-mono text-gray-500 shrink-0">{token.value}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 font-mono truncate">{token.name}</p>
                    <span className="text-xs text-gray-400">{token.updated}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
